﻿import {
  CalendarDays,
  LogOut,
  Menu,
  Receipt,
  Settings,
  Store,
  Tag,
  X,
} from "lucide-react";
import { useLocale, useT } from "@/lib/i18n";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, type ComponentType } from "react";
import { toast } from "sonner";

import { isRtlLocale, type Locale } from "@ticketnjoy/types";
import { LocaleSwitcher } from "@/components/locale-switcher";
import { ThemeToggle } from "@/components/theme-toggle";
import { useLogoutMutation } from "@/features/organizers/api";
import { cn } from "@/lib/utils";

interface DashboardShellProps {
  companyName: string;
  children: React.ReactNode;
}

interface NavItem {
  href: string;
  labelKey: "shops" | "events" | "orders" | "promotions" | "settings";
  icon: ComponentType<{ className?: string }>;
  matchPrefix: string;
}

const NAV_ITEMS: readonly NavItem[] = [
  { href: "/", labelKey: "shops", icon: Store, matchPrefix: "/shops" },
  { href: "/events", labelKey: "events", icon: CalendarDays, matchPrefix: "/events" },
  { href: "/orders", labelKey: "orders", icon: Receipt, matchPrefix: "/orders" },
  { href: "/promotions", labelKey: "promotions", icon: Tag, matchPrefix: "/promotions" },
  { href: "/settings", labelKey: "settings", icon: Settings, matchPrefix: "/settings" },
] as const;

function isActive(pathname: string, item: NavItem): boolean {
  if (item.href === "/") {
    return pathname === "/" || pathname.startsWith(item.matchPrefix);
  }
  return pathname.startsWith(item.matchPrefix);
}

/**
 * Authenticated shell — mobile-first ve RTL-aware.
 *
 * Desktop (lg+): sabit dar sidebar (56px), logical start kenarında.
 * Mobile: hamburger drawer — LTR'de soldan, RTL'de sağdan açılır.
 *
 * Logical CSS properties (start/end) Tailwind v4'te destekleniyor;
 * sidebar RTL'de otomatik karşı tarafa geçer.
 */
export function DashboardShell({ companyName, children }: DashboardShellProps) {
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  const logout = useLogoutMutation();
  const t = useT();
  const locale = useLocale() as Locale;
  const isRtl = isRtlLocale(locale);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [drawerOpen]);

  async function handleLogout() {
    try {
      await logout.mutateAsync();
      toast.success(t("auth.logout"));
      navigate("/login", { replace: true });
    } catch {
      toast.error(t("common.unexpectedError"));
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <DesktopSidebar pathname={pathname} t={t} />

      {drawerOpen && (
        <button
          type="button"
          aria-label={t("common.close")}
          onClick={() => setDrawerOpen(false)}
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
        />
      )}
      <MobileDrawer
        open={drawerOpen}
        pathname={pathname}
        companyName={companyName}
        isRtl={isRtl}
        onClose={() => setDrawerOpen(false)}
        onLogout={handleLogout}
        isLoggingOut={logout.isPending}
        t={t}
      />

      {/* Sidebar start tarafında — RTL'de ps- sağa geçer */}
      <div className="lg:ps-14">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b bg-background/80 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-foreground hover:bg-accent lg:hidden"
            aria-label={t("nav.shops")}
          >
            <Menu className="h-5 w-5" />
          </button>

          <Link
            to="/"
            className="flex items-center gap-2 font-semibold lg:hidden"
          >
            <BrandMark />
            <span>{t("app.name")}</span>
          </Link>

          <div className="ms-auto flex items-center gap-2">
            <LocaleSwitcher />
            <ThemeToggle />
            <span className="hidden max-w-[180px] truncate text-sm text-muted-foreground sm:inline">
              {companyName}
            </span>
            <button
              type="button"
              onClick={handleLogout}
              disabled={logout.isPending}
              className="inline-flex h-9 items-center gap-1.5 rounded-md px-2.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-60"
              aria-label={t("auth.logout")}
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">
                {logout.isPending ? t("auth.loggingOut") : t("auth.logout")}
              </span>
            </button>
          </div>
        </header>

        <main className="mx-auto w-full max-w-6xl px-4 py-6 sm:py-8">
          {children}
        </main>
      </div>
    </div>
  );
}

/* ─────────────────────────── Desktop sidebar ─────────────────────────── */

function DesktopSidebar({
  pathname,
  t,
}: {
  pathname: string;
  t: ReturnType<typeof useT>;
}) {
  return (
    <aside className="fixed inset-y-0 start-0 z-30 hidden w-14 flex-col items-center border-e border-sidebar-border bg-sidebar py-3 lg:flex">
      <Link
        to="/"
        className="mb-4 inline-flex h-9 w-9 items-center justify-center rounded-md text-primary transition-colors hover:bg-sidebar-accent"
        aria-label={t("app.name")}
      >
        <BrandMark />
      </Link>

      <nav className="flex flex-1 flex-col gap-1">
        {NAV_ITEMS.map((item) => (
          <SidebarLink
            key={item.href}
            item={item}
            label={t(`nav.${item.labelKey}`)}
            active={isActive(pathname, item)}
          />
        ))}
      </nav>
    </aside>
  );
}

function SidebarLink({
  item,
  label,
  active,
}: {
  item: NavItem;
  label: string;
  active: boolean;
}) {
  const Icon = item.icon;
  return (
    <Link
      to={item.href}
      className={cn(
        "group relative inline-flex h-10 w-10 items-center justify-center rounded-md text-sidebar-foreground transition-colors",
        active
          ? "bg-sidebar-accent text-sidebar-accent-foreground"
          : "text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
      )}
      aria-label={label}
    >
      <Icon className="h-4.5 w-4.5" />
      {/* Hover tooltip — start side'ın dışında, RTL'de otomatik sağdan sola */}
      <span className="pointer-events-none absolute start-full ms-3 hidden rounded-md bg-popover px-2 py-1 text-xs font-medium text-popover-foreground shadow-md ring-1 ring-border group-hover:inline-block whitespace-nowrap">
        {label}
      </span>
      {active ? (
        <span
          aria-hidden
          className="absolute start-0 h-5 w-0.5 rounded-e-full bg-primary"
        />
      ) : null}
    </Link>
  );
}

/* ─────────────────────────── Mobile drawer ─────────────────────────── */

interface MobileDrawerProps {
  open: boolean;
  pathname: string;
  companyName: string;
  isRtl: boolean;
  onClose: () => void;
  onLogout: () => void;
  isLoggingOut: boolean;
  t: ReturnType<typeof useT>;
}

function MobileDrawer({
  open,
  pathname,
  companyName,
  isRtl,
  onClose,
  onLogout,
  isLoggingOut,
  t,
}: MobileDrawerProps) {
  // translate-x açık/kapalı yönü dile bağlı — RTL'de zıt yöne kaydır.
  const closedTx = isRtl ? "translate-x-full" : "-translate-x-full";
  return (
    <aside
      className={cn(
        "fixed inset-y-0 start-0 z-50 flex w-72 max-w-[80vw] flex-col border-e border-sidebar-border bg-sidebar shadow-xl transition-transform duration-200 ease-out lg:hidden",
        open ? "translate-x-0" : closedTx,
      )}
      aria-hidden={!open}
    >
      <div className="flex h-14 items-center justify-between border-b border-sidebar-border px-4">
        <Link to="/" className="flex items-center gap-2 font-semibold">
          <BrandMark />
          <span>{t("app.name")}</span>
        </Link>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("common.close")}
          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-sidebar-accent"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <nav className="flex flex-1 flex-col gap-1 p-3">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = isActive(pathname, item);
          return (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                "inline-flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors",
                active
                  ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground"
                  : "text-sidebar-foreground hover:bg-sidebar-accent",
              )}
            >
              <Icon className="h-4 w-4" />
              {t(`nav.${item.labelKey}`)}
            </Link>
          );
        })}
      </nav>

      <div className="border-t border-sidebar-border p-3">
        <p className="mb-2 truncate px-3 text-xs text-muted-foreground">
          {companyName}
        </p>
        <button
          type="button"
          onClick={onLogout}
          disabled={isLoggingOut}
          className="inline-flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-sidebar-foreground transition-colors hover:bg-sidebar-accent disabled:opacity-60"
        >
          <LogOut className="h-4 w-4" />
          {isLoggingOut ? t("auth.loggingOut") : t("auth.logout")}
        </button>
      </div>
    </aside>
  );
}

/* ─────────────────────────── Brand mark ─────────────────────────── */

function BrandMark() {
  return (
    <svg viewBox="0 0 640 640" aria-hidden className="h-5 w-5">
      <rect x="0" y="0" width="640" height="640" rx="128" fill="currentColor" />
      <path d="M200 100H340L280 193.5L220 287L280 380.5H140L80 287L140 193.5L200 100Z" fill="white" />
      <path d="M440 540L300 540L360 446.5L420 353L360 259.5L500 259.5L560 353L500 446.5L440 540Z" fill="white" />
    </svg>
  );
}
